import { ReactNode } from 'react';

import { Event } from '@/feature/event-details-sbk/types';
import { HUD_HEIGHTS } from '@/feature/event-details-sbk/utils/get-hud-height';
import { getTeamBranding } from '@/feature/event-details-sbk/utils/get-team-colors';
import { TeamOrder } from '@/utils/sport-utils';

import { HudEvent } from './EventHudDetails';

export const hudEventHasPlayerImage = (event?: Pick<HudEvent, 'awayTeam' | 'homeTeam'> | null) =>
    !!event?.awayTeam.logoComponent || !!event?.homeTeam.logoComponent;

// Single source for the pregame HUD height — both the rendered HUD and the title crossfade threshold use it.
export const getPrematchHudHeight = (event?: Pick<HudEvent, 'awayTeam' | 'homeTeam'> | null) =>
    hudEventHasPlayerImage(event) ? HUD_HEIGHTS.prematchPlayerHeader : HUD_HEIGHTS.prematchHeader;

export const convertSbkEventToHudEvent = (event: Event): HudEvent => {
    return {
        startTime: event.start_time,
        homeTeam: {
            shortCode: event.home_team.short_code,
            alias: event.home_team.alias,
            logoUrl: event.home_team.logo_url,
            primaryColor: event.home_team.primary_color,
            secondaryColor: event.home_team.secondary_color,
        },
        awayTeam: {
            shortCode: event.away_team.short_code,
            alias: event.away_team.alias,
            logoUrl: event.away_team.logo_url,
            primaryColor: event.away_team.primary_color,
            secondaryColor: event.away_team.secondary_color,
        },
        sport: event.sport.name,
    };
};

// Resolves display-oriented data based on team order (left/right positions)
export const getTeamOrderData = (
    event: HudEvent,
    teamOrder: TeamOrder,
    winningSide: 'home' | 'away' | 'draw' | undefined,
    indicators?: { home?: ReactNode; away?: ReactNode }
) => {
    const [left, right] = teamOrder;
    const teams = { home: event.homeTeam, away: event.awayTeam };
    const colors = getTeamColors(event);

    return {
        left: {
            team: teams[left],
            colors: colors[left],
            scoreSide: left,
            isWinner: winningSide === left,
            indicator: indicators?.[left],
        },
        right: {
            team: teams[right],
            colors: colors[right],
            scoreSide: right,
            isWinner: winningSide === right,
            indicator: indicators?.[right],
        },
        draw: winningSide === 'draw',
    };
};

export const getTeamColors = (event?: Pick<HudEvent, 'awayTeam' | 'homeTeam'>) => ({
    home: getTeamBranding(event?.homeTeam),
    away: getTeamBranding(event?.awayTeam),
});
